/**
 * Local persistence for the web build.
 *
 * Same contract as the Expo app's SQLite layer, backed by localStorage. The
 * web build is the PWA / desktop companion; the phone app is where the volume
 * lives, so a simpler store is the right trade here.
 */

import { localDateString, type Client, type Order, type Product } from '../shared/domain';

const KEYS = {
  products: 'qd_products',
  clients: 'qd_clients',
  orders: 'qd_orders',
} as const;

/**
 * Reads never throw. A corrupt key, a full disk or Safari private mode should
 * cost one list, not the whole app — v1 let a JSON.parse failure take the
 * render down.
 */
function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // QuotaExceededError: usually a product photo pushing past the ~5 MB cap.
    return false;
  }
}

export const storage = {
  getProducts: (): Product[] => read<Product[]>(KEYS.products, []),
  saveProducts: (products: Product[]): boolean => write(KEYS.products, products),

  getClients: (): Client[] => read<Client[]>(KEYS.clients, []),
  saveClients: (clients: Client[]): boolean => write(KEYS.clients, clients),

  getOrders: (): Order[] => {
    const orders = read<Order[]>(KEYS.orders, []);
    // Repair v1 records on read: UTC date strings and missing paidAmount.
    return orders.map((o) => ({
      ...o,
      paidAmount: typeof o.paidAmount === 'number' ? o.paidAmount : o.totalAmount,
      dateString: o.timestamp ? localDateString(o.timestamp) : o.dateString,
    }));
  },
  saveOrders: (orders: Order[]): boolean => write(KEYS.orders, orders),

  /** Only clears this app's keys — v1 called localStorage.clear(). */
  clearAll: () => {
    for (const key of Object.values(KEYS)) {
      try { localStorage.removeItem(key); } catch { /* nothing to do */ }
    }
  },
};
